// Frais et débours avancés pour un dossier (table dossier_expenses, bucket expense-receipts).
// Droits : RLS par dossier (facturation visible) ; une ligne refacturée ne se modifie plus.
import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';
import { expenseAmounts, type ExpenseNature } from '../../lib/billing/expenses';

export type DossierExpense = Tables<'dossier_expenses'>;

const BUCKET = 'expense-receipts';

export interface ExpenseInput {
  dossier_id: string;
  nature: ExpenseNature;
  piece_date: string;
  label: string;
  supplier: string;
  piece_number: string;
  /** HT pour un frais, TTC pour un débours. */
  amount: number;
  vat_rate: number;
}

function toRow(input: ExpenseInput) {
  const amounts = expenseAmounts(input);
  const label = input.label.trim() || (input.nature === 'debours' ? `${input.supplier.trim()} — ${input.piece_number.trim()}` : '');
  return {
    dossier_id: input.dossier_id,
    nature: input.nature,
    piece_date: input.piece_date,
    label,
    supplier: input.supplier.trim() || null,
    piece_number: input.piece_number.trim() || null,
    ...amounts,
  };
}

export async function listExpenses(filters: { dossierId?: string } = {}): Promise<DossierExpense[]> {
  let query = supabase.from('dossier_expenses').select('*').order('piece_date', { ascending: false });
  if (filters.dossierId) query = query.eq('dossier_id', filters.dossierId);
  const { data, error } = await query;
  if (error) throw new Error(`Failed to list expenses: ${error.message}`);
  return data ?? [];
}

export async function createExpense(input: ExpenseInput): Promise<DossierExpense> {
  const { data, error } = await supabase.from('dossier_expenses').insert(toRow(input)).select('*').single();
  if (error) throw new Error(`Failed to create expense: ${error.message}`);
  return data;
}

export async function updateExpense(id: string, input: ExpenseInput): Promise<DossierExpense> {
  const { data, error } = await supabase.from('dossier_expenses').update(toRow(input)).eq('id', id).select('*').single();
  if (error) throw new Error(`Failed to update expense: ${error.message}`);
  return data;
}

export async function deleteExpense(expense: Pick<DossierExpense, 'id' | 'receipt_path'>): Promise<void> {
  const { error } = await supabase.from('dossier_expenses').delete().eq('id', expense.id);
  if (error) throw new Error(`Failed to delete expense: ${error.message}`);
  if (expense.receipt_path) await supabase.storage.from(BUCKET).remove([expense.receipt_path]);
}

/** Dépose (ou remplace) le justificatif : <dossier_id>/<expense_id>/<fichier>. */
export async function uploadExpenseReceipt(
  expense: Pick<DossierExpense, 'id' | 'dossier_id' | 'receipt_path'>,
  file: File,
): Promise<DossierExpense> {
  const safeName = file.name.replace(/[^\w.\-]+/g, '_').slice(-120) || 'justificatif';
  const path = `${expense.dossier_id}/${expense.id}/${Date.now()}-${safeName}`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type || 'application/octet-stream' });
  if (upErr) throw new Error(`Failed to upload receipt: ${upErr.message}`);
  const { data, error } = await supabase
    .from('dossier_expenses')
    .update({ receipt_path: path, receipt_name: file.name })
    .eq('id', expense.id)
    .select('*')
    .single();
  if (error) {
    await supabase.storage.from(BUCKET).remove([path]);
    throw new Error(`Failed to attach receipt: ${error.message}`);
  }
  if (expense.receipt_path && expense.receipt_path !== path) await supabase.storage.from(BUCKET).remove([expense.receipt_path]);
  return data;
}

/** Lien temporaire (15 min) vers le justificatif. */
export async function expenseReceiptUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 900);
  if (error || !data) throw new Error(`Failed to open receipt: ${error?.message ?? 'no url'}`);
  return data.signedUrl;
}

/** Contenu du justificatif (pour l'envoyer à Polaris). */
export async function downloadExpenseReceipt(path: string): Promise<Blob> {
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error || !data) throw new Error(`Failed to download receipt: ${error?.message ?? 'empty'}`);
  return data;
}

export async function setDossierDeboursMandate(dossierId: string, on: string | null, ref: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_dossier_debours_mandate', {
    p_dossier_id: dossierId,
    p_on: on as string,
    p_ref: ref as string,
  });
  if (error) throw new Error(`Failed to save mandate: ${error.message}`);
}
